import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import config from '../../../config/config';

export function EditShares() {
  const { id: customerId } = useParams();
  const navigate = useNavigate();

  const [customerDetails, setCustomerDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Edit States
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({
    firstname: '',
    lastname: '',
    category: '',
    feasabilillah: 0 // Dedicated field for Fisabilillah
  });

  const fetchFormData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.baseUrl}/api/moderator/customer-full-details/${customerId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch registration details');
      const data = await response.json();
      setCustomerDetails(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (customerId) fetchFormData();
  }, [customerId]);

  // Edit Handlers
  const startEditing = (share) => {
    setEditingId(share.share_id);
    setEditFormData({
      firstname: share.firstname || '',
      lastname: share.lastname || '',
      category: share.category || '',
      feasabilillah: share.feasabilillah || 0
    });
  };

  const handleSave = async (shareId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.baseUrl}/api/moderator/update-share/${shareId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editFormData)
      });

      if (!response.ok) throw new Error('Update failed');

      setEditingId(null);
      fetchFormData(); // Refresh data
      alert("Updated successfully!");
    } catch (err) {
      console.error('Error updating:', err);
      alert("Failed to update share.");
    }
  };

  const handlePrint = async (id, shareId) => {
    try {
      const response = await fetch(`${config.baseUrl}/api/moderator/generate-slip/${id}/${shareId}`);
      if (!response.ok) throw new Error('Failed to fetch payment data');
      const paymentData = await response.json();
      localStorage.setItem('temp_slip_data', JSON.stringify(paymentData));
      window.open('/slip', '_blank');
    } catch (error) {
      console.error('Error printing slip:', error);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
    </div>
  );

  if (error || !customerDetails) return (
    <div className="p-8 text-center text-red-600 bg-slate-50 min-h-screen">
      <div className="max-w-md mx-auto bg-white p-6 rounded-xl shadow-lg">
        <p className="font-bold text-lg">Data not found</p>
        <button onClick={() => navigate(-1)} className="mt-4 bg-indigo-600 text-white px-6 py-2 rounded-lg">Go Back</button>
      </div>
    </div>
  );

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto bg-white min-h-screen shadow-2xl my-10 rounded-3xl">
      {/* Header Info */}
      <div className="flex flex-col md:flex-row justify-between items-center border-b-4 border-indigo-800 pb-8 mb-10 gap-4">
        <h1 className="text-4xl font-black text-blue-900 uppercase">Edit Registration</h1>
        <div className="bg-indigo-800 text-white px-6 py-3 rounded-2xl text-center">
          <p className="text-[10px] uppercase font-black opacity-80">Customer ID</p>
          <p className="text-2xl font-mono font-black">{customerId}</p>
        </div>
      </div>

      {/* Customer Info Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        <div className="space-y-1">
          <label className="text-[11px] font-black text-slate-400 uppercase ml-1">Customer Name</label>
          <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-700 capitalize">{customerDetails.customer_name}</div>
        </div>
        <div className="space-y-1">
          <label className="text-[11px] font-black text-slate-400 uppercase ml-1">Phone</label>
          <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-blue-600">{customerDetails.phone}</div>
        </div>
        <div className="space-y-1">
          <label className="text-[11px] font-black text-slate-400 uppercase ml-1">Payment</label>
          <div className={`p-4 rounded-2xl font-black text-center border uppercase text-sm ${customerDetails.payment_status === 'paid' ? 'bg-green-100 text-green-700 border-green-200' : 'bg-amber-100 text-amber-700 border-amber-200'}`}>{customerDetails.payment_status}</div>
        </div>
      </div>

      {/* Shares Table */}
      <div className="overflow-hidden border border-indigo-100 rounded-3xl shadow-sm">
        <table className="w-full text-left">
          <thead className="bg-indigo-800 text-white">
            <tr className="text-[11px] font-black uppercase tracking-widest">
              <th className="px-4 py-5 text-center">#</th>
              <th className="px-6 py-5">Shareholder Details</th>
              <th className="px-6 py-5">Animal Info</th>
              <th className="px-4 py-5 text-center">Day</th>
              <th className="px-6 py-5 text-center">feasabilillah</th>
              <th className="px-6 py-5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {customerDetails.shares_data?.map((share, idx) => (
              <tr key={idx} className={`${editingId === share.share_id ? 'bg-indigo-50/50' : 'hover:bg-slate-50'}`}>
                <td className="px-4 py-4 text-center text-xs text-slate-400">{idx + 1}</td>

                {/* Name Edit */}
                <td className="px-6 py-4">
                  {editingId === share.share_id ? (
                    <div className="flex flex-col gap-2">
                      <input
                        className="border border-indigo-200 p-2 rounded-lg text-sm"
                        value={editFormData.firstname}
                        onChange={(e) => setEditFormData({ ...editFormData, firstname: e.target.value })}
                        placeholder="First Name"
                      />
                      <input
                        className="border border-indigo-200 p-2 rounded-lg text-sm"
                        value={editFormData.lastname}
                        onChange={(e) => setEditFormData({ ...editFormData, lastname: e.target.value })}
                        placeholder="Last Name"
                      />
                    </div>
                  ) : (
                    <div>
                      <p className="font-bold text-slate-800 capitalize">{`${share.firstname || ''} ${share.lastname || ''}`}</p>
                      <p className="text-[10px] text-slate-400 font-bold uppercase">Shareholder</p>
                    </div>
                  )}
                </td>

                <td className="px-6 py-4">
                  <div className="flex flex-col">
                    <span className="text-sm font-bold text-blue-900">{share.animal_type}</span>
                    <span className="text-[10px] font-bold text-slate-400 uppercase">{share.category}</span>
                  </div>
                </td>

                <td className="px-4 py-4 text-center font-black text-[10px] text-slate-600">DAY {share.slaughtering_day}</td>

                {/* Fisabilillah Checkbox */}
                <td className="px-6 py-4 text-center">
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded border-indigo-300 text-indigo-600 cursor-pointer"
                    checked={editingId === share.share_id ? editFormData.feasabilillah === true : share.feasabilillah === true}
                    disabled={editingId !== share.share_id}
                    onChange={(e) => setEditFormData({ ...editFormData, feasabilillah: e.target.checked ? true : false })}
                  />
                </td>

                <td className="px-6 py-4 text-right">
                  {editingId === share.share_id ? (
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => handleSave(share.share_id)} className="bg-green-600 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase">Save</button>
                      <button onClick={() => setEditingId(null)} className="bg-slate-400 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase">Cancel</button>
                    </div>
                  ) : (
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => startEditing(share)} className="bg-amber-500 text-white px-4 py-2 rounded-xl font-black text-[10px] uppercase">Edit</button>
                      <button onClick={() => handlePrint(customerDetails.id, share.share_id)} className="bg-indigo-800 text-white px-4 py-2 rounded-xl font-black text-[10px] uppercase">Print</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-10 text-center">
        <button onClick={() => navigate(-1)} className="bg-slate-800 text-white px-10 py-4 rounded-2xl font-black uppercase text-sm tracking-widest">Go Back</button>
      </div>
    </div>
  );
}